import { Close } from "@mui/icons-material";
import {
  Box, Dialog, DialogContent,
  IconButton,
  ImageList,
  ImageListItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  setContactForEditStatus,
  setContactForStatusList,
  setContactStatusForEditStatus,
} from 'reducers/slices/UIReducer';
import { useAppSelector } from 'reducers/hooks';
import { getContactName, getContactStatus, getContactUnavailability, ScheduledContactStatus } from 'utils/accounts';
import { useEffect, useState } from "react";
import ContactStatusAlert from "./contactItem/ContactStatusAlert";
import { getScheduledStatuses } from "api/contacts";
import AnimatedButton from "components/buttons/AnimatedButton";
import ContactStatusDeleteDialog from "./ContactStatusDeleteDialog";
import ContactStatusArchiveDialog from './ContactStatusArchiveDialog';
import { getContactDetail } from '../../../../api/accounts';
import { updateContact } from '../../../../reducers/slices/accountPanelReducer';

const ContactStatusListDialog = () => {
  const contactForStatusList = useAppSelector(state => state.ui.contactForStatusList)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [contactStatuses, setContactStatuses] = useState<ScheduledContactStatus[]>([])
  const [isConfirmingDelete, setIsConfirmingDelete] = useState<ScheduledContactStatus | null>(null)
  const [isConfirmingArchive, setIsConfirmingArchive] = useState<ScheduledContactStatus | null>(null)

  const dispatch = useDispatch()

  useEffect(() => {
    if (contactForStatusList) {
      getStatuses()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactForStatusList])

  const getStatuses = () => {
    if (contactForStatusList) {
      setIsLoading(true)
      getScheduledStatuses(contactForStatusList.accountId, contactForStatusList.id).then((res) => {
        setContactStatuses(res.data || [])
      }).finally(() => setIsLoading(false))
    }
  }

  const handleDeleteCallback = () => {
    setIsConfirmingDelete(null)
    getStatuses()
  }

  const handleArchiveCallback = () => {
    setIsConfirmingArchive(null)
    if (contactForStatusList) {
      getContactDetail(contactForStatusList.id).then((res) => {
        if (contactForStatusList) {
          dispatch(setContactForStatusList({
            ...contactForStatusList,
            ...res.data,
          }));
        }
        dispatch(updateContact({
          id: res.data.id,
          update: res.data,
        }));
      });
    }
  }

  const handleNew = () => {
    if (contactForStatusList) {
      dispatch(setContactStatusForEditStatus({
        contact: contactForStatusList,
        // @ts-ignore
        contactStatus: { id: 'new' },
      }))
    }
  }

  const handleClose = () => {
    dispatch(setContactForStatusList(null))
  }

  const currentUnavailability = contactForStatusList
    ? getContactUnavailability(
      getContactStatus(contactForStatusList, true),
      contactForStatusList
    )
    : '';

  const hasCurrentStatus = contactForStatusList?.displayAsAlert || !!currentUnavailability;

  return (
    <Dialog open={!!contactForStatusList} onClose={handleClose} maxWidth={false}>
      <ContactStatusDeleteDialog
        contactStatus={isConfirmingDelete}
        contact={contactForStatusList}
        closeDelete={() => setIsConfirmingDelete(null)}
        onDeleteCallback={handleDeleteCallback}
      />

      <ContactStatusArchiveDialog
        contactStatus={isConfirmingArchive}
        contact={contactForStatusList}
        closeArchive={() => setIsConfirmingArchive(null)}
        onArchiveCallback={handleArchiveCallback}
      />

      <DialogContent
        sx={{
          width: 740,
          minHeight: 400,
          pb: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Box position="relative" width="100%">
          <Typography sx={{ fontWeight: 600, fontSize: 20, pr: 4 }}>
            {contactForStatusList ? getContactName(contactForStatusList) : ''}
            {' - Call Status'}
          </Typography>
          <IconButton sx={{ position: 'absolute', right: -4, top: -4 }} onClick={handleClose}>
            <Close sx={{ color: 'black' }} />
          </IconButton>
        </Box>

        <Stack mt={1} direction="row" justifyContent="space-between">
          <Typography sx={{ fontWeight: 600, fontSize: 20 }}>
            Current Status
          </Typography>

          {<AnimatedButton
            onClick={() => hasCurrentStatus ? handleNew() : dispatch(setContactForEditStatus(contactForStatusList))}
            label='+ Status'
            sx={{
              width: 200,
              borderRadius: '15px',
            }}
          />}
      </Stack>

        <ImageList variant="masonry" cols={2} gap={8}>
          {hasCurrentStatus && <ImageListItem key='1'>
            {contactForStatusList && <ContactStatusAlert
              contactStatus={getContactStatus(contactForStatusList, true)}
              onEdit={() => dispatch(setContactForEditStatus(contactForStatusList))}
              onDelete={contactForStatusList?.displayAsAlert ? () => setIsConfirmingArchive(getContactStatus(contactForStatusList, true)) : undefined}
              isArchivable={true}
            />}
          </ImageListItem>}
        </ImageList>

        {!hasCurrentStatus && <Typography textAlign="center" sx={{ width: '100%' }}>No Current Status Found.</Typography>}

        <Stack mt={2} direction="row" justifyContent="space-between">
          <Typography sx={{ fontWeight: 600, fontSize: 20, pr: 4 }}>
            Upcoming Statuses
          </Typography>

          {!hasCurrentStatus && <AnimatedButton
            onClick={handleNew}
            label={'+ Status'}
            sx={{
              width: 200,
              borderRadius: '15px',
            }}
          />}
        </Stack>



        {isLoading &&
          <ImageList variant="masonry" cols={2} gap={8}>
            {[...new Array(6)].map((_e, i) => (
              <ImageListItem key={i}>
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '5px' }} />
              </ImageListItem>
            ))}
          </ImageList>
        }

        {(!isLoading && contactForStatusList) &&
          <ImageList variant="masonry" cols={2} gap={8} sx={{ scrollbarGutter: 'stable', pr: 1, mr: '-18px' }}>
            {contactStatuses.map(contactStatus => (
              <ImageListItem key={contactStatus.id}>
                <Box key={contactStatus.id}>
                  <ContactStatusAlert
                    contactStatus={contactStatus}
                    // @ts-ignore
                    onEdit={() => dispatch(setContactStatusForEditStatus({ contactStatus, contact: contactForStatusList }))}
                    onDelete={() => setIsConfirmingDelete(contactStatus)}
                  />
                </Box>
              </ImageListItem>
            ))}
          </ImageList>
        }

        {(!isLoading && contactForStatusList && !contactStatuses.length) &&
          <Typography textAlign="center" sx={{ width: '100%', mt: 3 }}>No Upcoming Statuses Found.</Typography>
        }

      </DialogContent>
    </Dialog>
  );
};

export default ContactStatusListDialog;
